import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header, Footer } from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import {
  Gift, EnvelopeSimple, ArrowLeft, MagnifyingGlass, CheckCircle, Warning,
} from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * AdminGrantUnlockPage — super-admin tool that grants ONE free unlock of a
 * paywalled tool to a specific signed-up user. Useful for support reverts,
 * influencer comps, prize fulfilment, and CS goodwill credits.
 *
 * Backend contract:
 *   GET  /api/super-admin/grant-free-unlock/tools  → {tools:[{key,label,price}]}
 *   POST /api/super-admin/grant-free-unlock {email, tool}
 */
export default function AdminGrantUnlockPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [tool, setTool] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user?.is_super_admin) return;
    (async () => {
      try {
        const { data } = await api.get("/super-admin/grant-free-unlock/tools");
        setTools(data.tools || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load tools.");
      } finally {
        setLoading(false);
      }
    })();
  }, [authLoading, user]);

  const toolLookup = useMemo(() => {
    const m = new Map();
    for (const t of tools) m.set(t.key, t);
    return m;
  }, [tools]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!email.trim() || !tool) {
      toast.error("Please provide an email and pick a tool.");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/super-admin/grant-free-unlock", {
        email: email.trim(),
        tool,
      });
      setResult(data);
      if (data.already_unlocked) {
        toast.message("Already unlocked", { description: data.message });
      } else {
        toast.success(data.message);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "Failed to grant unlock.";
      toast.error(detail);
      setResult({ ok: false, error: detail });
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    if (!email.trim() || !tool) {
      toast.error("Enter the same email + pick the tool you want to revoke.");
      return;
    }
    if (!window.confirm(
      `Revoke '${toolLookup.get(tool)?.label || tool}' for ${email.trim()}?\n\n` +
      `This removes ONLY admin-granted unlocks. Paid unlocks are protected and will remain.`
    )) return;
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/super-admin/grant-free-unlock/revoke", {
        email: email.trim(),
        tool,
      });
      setResult(data);
      if ((data.removed_count || 0) === 0) {
        toast.message("Nothing to revoke", {
          description: "This user has no admin-granted unlock for this tool.",
        });
      } else {
        toast.success(data.message);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "Failed to revoke unlock.";
      toast.error(detail);
      setResult({ ok: false, error: detail });
    } finally {
      setBusy(false);
    }
  };

  if (!authLoading && !user?.is_super_admin) {
    return (
      <div className="min-h-[100dvh] bg-[#FAF4E8] flex flex-col">
        <Header />
        <main className="flex-1 max-w-xl mx-auto px-4 py-16 text-center" data-testid="grant-gated">
          <h1 className="font-serif-display text-3xl">Super Admin only</h1>
          <p className="text-muted mt-3">This tool is restricted.</p>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-[#FAF4E8] flex flex-col">
      <Header />
      <main className="flex-1 max-w-2xl w-full mx-auto px-4 py-10" data-testid="grant-unlock-page">
        <button
          type="button"
          onClick={() => navigate("/admin/users")}
          className="inline-flex items-center gap-1.5 text-sm text-muted hover:text-[#3F2D1B] mb-6"
          data-testid="back-to-admin-users"
        >
          <ArrowLeft size={14} /> Back to users
        </button>

        <h1 className="font-serif-display text-3xl flex items-center gap-2 text-[#3F2D1B]">
          <Gift size={24} weight="fill" className="text-amber-500" />
          Grant a free report
        </h1>
        <p className="text-muted mt-2 text-sm leading-relaxed">
          Unlocks one paywalled tool for a specific signed-up user — on their
          saved birth-details fingerprint. They&apos;ll see the unlocked
          experience the next time they open that tool. Idempotent: re-granting
          the same (user, tool) is a no-op.
        </p>

        <form onSubmit={submit} className="mt-8 space-y-5 bg-white border border-amber-200/60 rounded-2xl p-6 shadow-sm">
          <div>
            <label htmlFor="grant-email" className="block text-xs font-bold uppercase tracking-widest text-[#92400E]">
              User email <span className="text-rose-600">*</span>
            </label>
            <div className="mt-1.5 relative">
              <EnvelopeSimple size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#92400E]/60" />
              <input
                id="grant-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="user@example.com"
                className="w-full pl-9 pr-3 py-2.5 text-sm bg-white border border-amber-300 rounded-md focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500/30"
                data-testid="grant-email-input"
                autoComplete="off"
              />
            </div>
            <p className="text-[11px] text-muted mt-1">The address they used to sign in with Google.</p>
          </div>

          <div>
            <label htmlFor="grant-tool" className="block text-xs font-bold uppercase tracking-widest text-[#92400E]">
              Which report? <span className="text-rose-600">*</span>
            </label>
            <select
              id="grant-tool"
              required
              value={tool}
              onChange={(e) => setTool(e.target.value)}
              disabled={loading}
              className="mt-1.5 w-full px-3 py-2.5 text-sm bg-white border border-amber-300 rounded-md focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500/30 disabled:opacity-50"
              data-testid="grant-tool-select"
            >
              <option value="">{loading ? "Loading tools…" : "— Select a tool —"}</option>
              {tools.map((t) => (
                <option key={t.key} value={t.key}>
                  {t.label} (₹{t.price})
                </option>
              ))}
            </select>
            {tool && toolLookup.get(tool) && (
              <p className="text-[11px] text-muted mt-1">
                Normally costs ₹{toolLookup.get(tool).price} — this grant will make it free for this user&apos;s chart.
              </p>
            )}
          </div>

          <div className="rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2 flex items-start gap-2">
            <Warning size={14} weight="fill" className="text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-[11px] text-[#3F2D1B]/80 leading-relaxed">
              The user must have completed onboarding (date / time / place set on their
              profile). If not, you&apos;ll see a clear error and can ask them to fill it
              in first.
            </p>
          </div>

          <button
            type="submit"
            disabled={busy || loading || !email.trim() || !tool}
            className="btn-primary w-full rounded-full py-2.5 text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="grant-submit-btn"
          >
            <Gift size={14} weight="fill" />
            {busy ? "Granting…" : "Grant 1 free unlock"}
          </button>

          <button
            type="button"
            onClick={revoke}
            disabled={busy || loading || !email.trim() || !tool}
            className="w-full rounded-full py-2 text-xs font-medium tracking-wide uppercase border border-rose-300 text-rose-700 bg-rose-50 hover:bg-rose-100 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="revoke-btn"
            title="Removes only admin-granted unlocks; paid unlocks are protected."
          >
            {busy ? "Revoking…" : "Undo · Revoke this granted unlock"}
          </button>
        </form>

        {result?.ok && (
          <div
            className={`mt-6 rounded-xl border px-5 py-4 flex items-start gap-3 ${
              result.already_unlocked
                ? "border-amber-300 bg-amber-50/70"
                : "border-emerald-300 bg-emerald-50/70"
            }`}
            data-testid="grant-result"
          >
            <CheckCircle
              size={20}
              weight="fill"
              className={result.already_unlocked ? "text-amber-600" : "text-emerald-600"}
            />
            <div className="min-w-0">
              <p className="font-semibold text-sm text-[#1A1410]">{result.message}</p>
              <p className="text-[11px] text-muted mt-1 font-mono break-all">
                user: {result.user_name || result.user_id} · tool: {result.tool_label} · fp: {result.fingerprint}
              </p>
            </div>
          </div>
        )}

        {result && result.ok === false && result.error && (
          <div
            className="mt-6 rounded-xl border border-rose-300 bg-rose-50/70 px-5 py-4 flex items-start gap-3"
            data-testid="grant-error"
          >
            <Warning size={20} weight="fill" className="text-rose-600" />
            <p className="text-sm text-rose-800">{result.error}</p>
          </div>
        )}

        <details className="mt-8 text-xs text-muted">
          <summary className="cursor-pointer">How does this work?</summary>
          <div className="mt-3 space-y-2 leading-relaxed">
            <p>
              Each tool unlock is keyed on (user, tool, chart-fingerprint). A grant
              creates a real tool_unlock record — same as if the user had paid via
              wallet or Razorpay — but with <code>paid_via=&quot;admin_grant&quot;</code>
              and <code>amount=0</code>, attributed to your email in the <code>ref</code> field.
            </p>
            <p>
              Re-granting the same (email, tool) doesn&apos;t double up — the endpoint
              returns <code>already_unlocked: true</code>.
            </p>
          </div>
        </details>
      </main>
      <Footer />
    </div>
  );
}
